import { ShieldAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { authApi } from '../features/auth/authApi';
import { createHubConnection } from '../lib/signalr';
import { useAuth } from './AuthContext';
import { currentPageAffectedBy } from './pageGates';

/** Mirrors `SessionHub.PermissionsChanged` on the server. */
const PERMISSIONS_CHANGED = 'PermissionsChanged';

/** What changed against the set this screen was drawn with. */
export interface PermissionChange {
  added: string[];
  removed: string[];
}

/**
 * Listens for the server saying this account's permissions changed, and tells the reader.
 *
 * **The screen keeps the permissions it signed in with** — it does not quietly adopt the new
 * set, because a page whose buttons rearrange themselves mid-transaction is worse than one
 * that says so. The server re-checks every request against the CURRENT set, so nothing the
 * reader lost will work whatever the screen still shows; signing in again is what brings the
 * screen into line.
 *
 * - **The page on screen is affected** (a changed key belongs to the module the page is gated
 *   on) → straight to `/permissions-changed`, which offers only signing out.
 * - **It is not** → a notice the reader may put off, so a transaction in progress elsewhere can
 *   be finished.
 * - **Nothing actually changed for this account** (a role edit its own grant rows override) →
 *   nothing is shown.
 *
 * The push carries no permissions: the new set is read from `/api/auth/me` with this
 * browser's own token, so the answer is the server's and is about this account only.
 */
export function PermissionChangeWatcher() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [notice, setNotice] = useState<PermissionChange | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  // Read inside the push handler, which outlives renders; written from an effect only.
  const grantedRef = useRef<readonly string[]>([]);
  useEffect(() => {
    grantedRef.current = user?.permissions ?? [];
  }, [user]);

  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;

    const connection = createHubConnection('/hubs/session');

    let disposed = false;

    connection.on(PERMISSIONS_CHANGED, () => {
      void (async () => {
        let next: string[];
        try {
          next = (await authApi.me()).permissions;
        } catch {
          // A session that cannot answer /me is already on its way to the sign-in page.
          return;
        }

        if (disposed) return;

        const before = new Set(grantedRef.current);
        const after = new Set(next);
        const change: PermissionChange = {
          added: next.filter((key) => !before.has(key)),
          removed: grantedRef.current.filter((key) => !after.has(key)),
        };

        if (change.added.length === 0 && change.removed.length === 0) return;

        if (currentPageAffectedBy([...change.added, ...change.removed])) {
          setNotice(null);
          navigate('/permissions-changed', { replace: true, state: change });
        } else {
          setNotice(change);
        }
      })();
    });

    // A failed connect is not an error the reader can act on; the server still enforces every
    // request, and withAutomaticReconnect covers a dropped connection once it is up.
    connection.start().catch(() => undefined);

    return () => {
      disposed = true;
      void connection.stop();
    };
  }, [userId, navigate]);

  if (!notice) return null;

  return (
    <Modal
      open
      onClose={() => setNotice(null)}
      title="Your permissions have changed"
      size="md"
      id="permissions-changed-modal"
      footer={
        <>
          <Button id="permissions-changed-later" variant="secondary" onClick={() => setNotice(null)}>
            Later
          </Button>

          {/* Not a form submit — it ends the session, which is the act the title asks for. */}
          <Button
            id="permissions-changed-sign-out"
            variant="primary"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true);
              void logout();
            }}
          >
            Sign out
          </Button>
        </>
      }
    >
      <div className="flex gap-3">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="flex flex-col gap-2 text-sm text-slate-600">
          <p>
            An administrator changed your role or what it grants. This page is not affected, so
            you can finish what you are doing here.
          </p>
          <p>Sign out and sign in again to see the change everywhere else.</p>
          <ChangeCounts change={notice} />
        </div>
      </div>
    </Modal>
  );
}

/** `2 permissions added · 1 removed` — counts only; the keys are not the reader's words. */
export function ChangeCounts({ change }: { change: PermissionChange }) {
  const parts = [
    change.added.length > 0 ? `${change.added.length} added` : '',
    change.removed.length > 0 ? `${change.removed.length} removed` : '',
  ].filter(Boolean);

  const total = change.added.length + change.removed.length;

  return (
    <p className="text-xs text-slate-500">
      <span className="font-mono tabular-nums">{total}</span>{' '}
      {total === 1 ? 'permission' : 'permissions'} changed: {parts.join(' · ')}
    </p>
  );
}
